import { useState } from 'react';
import { chakraComponents } from 'chakra-react-select';
import PropTypes from 'prop-types';
import { Box, Button, ButtonGroup, Checkbox, chakra, Spinner } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiTrash } from 'react-icons/fi';

export const OptionNameId = (props) => {
  const { name, id } = props.data;
  return (
    <chakraComponents.Option {...props}>
      <chakra.div>
        <chakra.div aria-label={name}>{name}</chakra.div>
        <chakra.div style={{ color: 'darkgray' }}>{id}</chakra.div>
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionNameId.propTypes = {
  data: PropTypes.object,
};

export const OptionSystemId = (props) => {
  const { label, is_active } = props.data;
  return (
    <chakraComponents.Option {...props}>
      <chakra.div>
        <chakra.div aria-label={label}>{label}</chakra.div>
        <chakra.div style={{ color: 'darkgray' }}>{is_active ? 'Active' : 'Inactive'}</chakra.div>
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionSystemId.propTypes = {
  data: PropTypes.object,
};

export const OptionRoutes = (props) => {
  const { name, description, path, is_deprecated } = props.data;
  return (
    <chakraComponents.Option {...props} isDisabled={is_deprecated}>
      <chakra.div>
        <chakra.div aria-label={name}>{name}</chakra.div>
        {description && <chakra.div style={{ color: 'darkgray' }}>{description}</chakra.div>}
        <chakra.div style={{ color: 'darkgray' }}>{path}</chakra.div>
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionRoutes.propTypes = {
  data: PropTypes.object,
};

export const OptionName = (props) => {
  const { name, city, state } = props.data;
  return (
    <chakraComponents.Option {...props}>
      <chakra.div>
        <chakra.div aria-label={name}>{name}</chakra.div>
        <chakra.span style={{ color: 'darkgray' }}>{`${city}, ${state}`}</chakra.span>
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionName.propTypes = {
  data: PropTypes.object,
};

export const OptionShortName = (props) => {
  const { short_name, division } = props.data;

  const selectId = (id) => {
    return props.data.id === id;
  };

  const selectAllStyle = {
    backgroundColor: 'brand.100',
    cursor: 'pointer',
  };

  return (
    <>
      <chakraComponents.Option {...props}>
        <chakra.div>
          <chakra.div display="flex">
            {selectId('select_all') && (
              <Checkbox
                id={props.data.id}
                size="md"
                colorScheme="cyan"
                isChecked={props.data.checked}
                marginRight="8px"
                marginLeft="-25px"
                alignItems="center"
                isDisabled={props.data.disabled}
              />
            )}
            <chakra.div
              aria-label={short_name}
              aria-disabled={props.isDisabled}
              data-disabled={props.isDisabled}
              sx={selectId('select_all') || selectId('deselect_all') ? selectAllStyle : null}
            >
              {short_name}
            </chakra.div>
          </chakra.div>
          <chakra.span style={{ color: 'darkgray' }}>{division}</chakra.span>
        </chakra.div>
      </chakraComponents.Option>
      {selectId('select_all') || selectId('deselect_all') ? <div style={{ borderTop: '1px solid #ddd', marginTop: '2px' }} /> : null}
    </>
  );
};
OptionShortName.propTypes = {
  data: PropTypes.object,
};

export const OptionFirstLastName = (props) => {
  const { first_name, last_name, email } = props.data;
  return (
    <chakraComponents.Option {...props}>
      <chakra.div>
        <chakra.div aria-label={first_name}>{first_name + ' ' + last_name}</chakra.div>
        <chakra.div style={{ color: 'darkgray' }}>{email}</chakra.div>
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionFirstLastName.propTypes = {
  data: PropTypes.object,
};

export const OptionRole = (props) => {
  const { role, label } = props.data;
  return (
    <chakraComponents.Option {...props}>
      <chakra.div>
        <chakra.div aria-label={label}>{label}</chakra.div>
        <chakra.div style={{ color: 'darkgray' }}>{role}</chakra.div>
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionRole.propTypes = {
  data: PropTypes.object,
};

export const OptionFirstLastNameDivision = (props) => {
  const { first_name, last_name, email, division } = props.data;
  const desc = division ? `${email} - ${division}` : email;
  return (
    <chakraComponents.Option {...props}>
      <chakra.div>
        <chakra.div aria-label={first_name}>{first_name + ' ' + last_name}</chakra.div>
        <chakra.div style={{ color: 'darkgray' }}>{desc}</chakra.div>
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionFirstLastNameDivision.propTypes = {
  data: PropTypes.object,
};

export const OptionNameCityAddress = (props) => {
  const { label, city, address, state, facility_type } = props.data;
  return (
    <chakraComponents.Option {...props}>
      <chakra.div>
        <chakra.div aria-label={label}>{label}</chakra.div>
        <chakra.div sx={{ color: 'gray.400' }}>{address && city && state ? address + ', ' + city + ', ' + state : 'No Address found'}</chakra.div>
        {facility_type && (
          <chakra.div fontSize="sm" style={{ color: 'darkgray' }}>
            {'Type: ' + facility_type}
          </chakra.div>
        )}
      </chakra.div>
    </chakraComponents.Option>
  );
};
OptionNameCityAddress.propTypes = {
  data: PropTypes.object,
};

export const OptionCategoryDelete = (props) => {
  const { value, label } = props.data;
  const { onRequestDelete, onConfirmDelete, onCancelDelete, pendingDeleteId, deletingIds, deleteErrors } = props.selectProps;
  const [isHovered, setIsHovered] = useState(false);

  const isPending = pendingDeleteId === value;
  const isDeleting = deletingIds?.has(value);
  const errorMsg = deleteErrors?.[value];

  if (isPending || isDeleting) {
    return (
      <Box
        ref={props.innerRef}
        {...props.innerProps}
        onClick={undefined}
        px={4}
        py={2}
        bg="orange.50"
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="gray.200"
        display="flex"
        alignItems="center"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}
        >
          {isDeleting ? (
            <Spinner size="xs" color="orange.500" />
          ) : (
            <>
              <chakra.span fontSize="sm" color="gray.700">
                Remove &ldquo;{label}&rdquo;?
              </chakra.span>
              <ButtonGroup size="xs" isAttached variant="outline">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfirmDelete(value);
                  }}
                >
                  Yes
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelDelete();
                  }}
                >
                  No
                </Button>
              </ButtonGroup>
            </>
          )}
        </motion.div>
      </Box>
    );
  }

  return (
    <chakraComponents.Option {...props}>
      <chakra.div
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <chakra.div flex="1">
          {label}
          {errorMsg && (
            <chakra.span fontSize="xs" color="red.500" ml={2} fontStyle="italic">
              — {errorMsg}
            </chakra.span>
          )}
        </chakra.div>
        <AnimatePresence>
          {isHovered && (
            <motion.span
              key="trash"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRequestDelete(value);
              }}
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginLeft: '8px' }}
            >
              <chakra.span color="gray.400" _hover={{ color: 'red.500' }} display="flex">
                <FiTrash size={13} />
              </chakra.span>
            </motion.span>
          )}
        </AnimatePresence>
      </chakra.div>
    </chakraComponents.Option>
  );
};

OptionCategoryDelete.propTypes = {
  data: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  innerProps: PropTypes.object,
  selectProps: PropTypes.object,
};
